import React, { Fragment, useEffect, useState } from 'react';
import { View } from 'react-native';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { useViewerModel } from '@models/Viewer/ViewerModel';
import { screens } from '@navigation';
import {
  BaseText,
  Button,
  ButtonStyleEnum,
  Icon,
  ScrollView,
  Separator,
} from '@components';
import { IS_WEB } from '@utils';
import { ShareService, ToastService } from '@services';
import {
  useLink,
  useNavOptionsSet,
  usePersonalizationNavigation,
} from '@hooks';
import { useAppModel } from '@models/App/AppModel';

const STEP_INDICATOR_SIZE = 54;

const STEPS = [
  {
    labelKey: 'inviteSuggestion.signUp',
    icon: 'edit-light',
    completed: true,
  },
  {
    labelKey: 'inviteSuggestion.referFriend',
    icon: 'user-three-light',
    completed: true,
  },
  {
    labelKey: 'inviteSuggestion.success',
    icon: 'done-circule-light',
    completed: false,
  },
];

const Container = styled(ScrollView)({
  flex: 1,
  backgroundColor: '$background0',
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: ['$6', null],
    pt: theme.space.$10 + props.top,
    pb: theme.space.$10 + props.bottom,
    alignItems: 'center',
    justifyContent: [null, 'center'],
  });
}

const ContentContainer = styled(View)({
  flex: 1,
  maxWidth: [null, 440],
  justifyContent: IS_WEB ? 'center' : 'space-between',
});

const StepsContainer = styled(View)({
  height: 90,
  flexDirection: 'row',
  alignItems: 'flex-end',
});

const StepItem = styled(View)({});

interface IStepIndicatorStyleProps {
  completed?: boolean;
}

const StepIndicator = styled(View)(
  (props: IStepIndicatorStyleProps) => ({
    justifyContent: 'center',
    alignItems: 'center',
    size: STEP_INDICATOR_SIZE,
    borderRadius: STEP_INDICATOR_SIZE / 2,
    borderWidth: 1,
    borderColor: props.completed ? '$primary300' : '$background30',
  }),
);

const StepLabelContainer = styled(View)({
  position: 'absolute',
  width: STEP_INDICATOR_SIZE * 2,
  left: -STEP_INDICATOR_SIZE / 2,
  bottom: STEP_INDICATOR_SIZE + 16,
});

const StepLabel = styled(BaseText)({
  color: '$text400',
  variant: 'text.large',
  fontWeight: 'semi',
  textAlign: 'center',
});

const StepSeparator = styled(View)({
  flex: 1,
  mx: '$4',
  height: 1,
  bottom: STEP_INDICATOR_SIZE / 2,
  bg: '$background30',
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
  textAlign: 'center',
});

const Subtitle = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
  textAlign: 'center',
});

const ButtonsContainer = styled(View)({
  width: [null, 344],
  alignSelf: [null, 'center'],
});

export const InviteSuggestionScreen = observer(() => {
  const [isLinkShared, setIsLinkShared] = useState(false);
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const app = useAppModel();
  const insets = useSafeAreaInsets();
  const { buildFullLink } = useLink();
  const { navigateToNextScreen, navigateToHome } =
    usePersonalizationNavigation();

  useNavOptionsSet({ title: t('inviteSuggestion.title') });

  useEffect(() => {
    // On web user can reach this page by entering route in address bar
    // We check if need to suggest user invite a friend if no need we will redirect him to
    // home page
    if (IS_WEB && !app.isNeedToSuggestInvite) {
      navigateToHome();
    }
  }, []);

  async function shareInviteLink() {
    try {
      const invite = viewer.currentUser?.invite.authorizationFlow;

      const inviteLink = buildFullLink({
        screen: screens.SwitchNavigator,
        params: {
          screen: screens.AuthNavigator,
          params: {
            screen: screens.Referral,
            params: { invite },
          },
        },
      });

      const link = await viewer.createShortLink.run({
        link: inviteLink,
      });

      if (!link) {
        throw new Error(t('errors.sharedLink'));
      }

      await ShareService.shareUrl(
        { url: link.shortLink },
        {
          clipboardMessage: t('tutorial.linkCopied'),
        },
      );

      setIsLinkShared(true);
    } catch (error) {
      const message = (error as Error).message;
      ToastService.showError(message ?? t('errors.default'));
    }
  }

  return (
    <Container contentContainerSx={contentContainerStyle(insets)}>
      <ContentContainer>
        <View>
          <StepsContainer>
            {STEPS.map((step, index) => (
              <Fragment key={index}>
                {index !== 0 && <StepSeparator />}

                <StepItem>
                  <StepIndicator completed={step.completed}>
                    <Icon name={step.icon} size={24} />
                  </StepIndicator>
                  <StepLabelContainer>
                    <StepLabel>{t(step.labelKey)}</StepLabel>
                  </StepLabelContainer>
                </StepItem>
              </Fragment>
            ))}
          </StepsContainer>

          <Separator size="$9" />
          <Title>{`${t('inviteSuggestion.title')} 🤝`}</Title>

          <Separator size="$5" />
          <Subtitle>
            {t('inviteSuggestion.subtitle', {
              context: viewer.currentUser?.tariffPlan.id,
            })}
          </Subtitle>
          <Separator size="$7" />
        </View>

        <ButtonsContainer>
          <Button
            onPress={shareInviteLink}
            isLoading={viewer.createShortLink.inProgress}
          >
            {t('inviteSuggestion.button', {
              context: IS_WEB ? 'copyLink' : 'share',
            })}
          </Button>

          <Separator size="$5" />
          <Button
            buttonStyle={ButtonStyleEnum.Outline}
            onPress={navigateToNextScreen}
          >
            {t('inviteSuggestion.button', {
              context: isLinkShared ? 'continue' : 'skip',
            })}
          </Button>
        </ButtonsContainer>
      </ContentContainer>
    </Container>
  );
});
